import React, { Component } from "react";
import { Container, Row, Col, Alert, Button, ButtonGroup } from "react-bootstrap";
import { GoX } from "react-icons/go";
import Navigation from "context/nav";
import Selector from "./selector/index";
import { cascadeOffset } from "../controls/library/placement";
import LibraryPane from "../controls/library/index";
import DetailsPane from "./details/index";
import EditorPane from "./editor";
import CheckerPane from "./checker";
import Converter from "./converter";
import Preferences from "context/preferences";

// The registry key, and the words the Blob page's confirmation uses to name this surface.
const UNSAVED_WORK = "the workflow editor";

export default class EditorShell extends Component {
  constructor(props) {
    super(props);
    const scope = { orgId: this.props.org.id, blobId: this.props.blob.id };

    this.state = {
      // Global loading success status (manifest, credentials, etc)
      fatal: false,
      fatalMessage: null,
      // Definition operations progress flags
      progress: false,
      progressMessage: null,
      error: false,
      errorMessage: null,
      // Workflow manifest object
      manifest: null,
      // The list of definition items
      definitions: null,
      // User credentials
      credentials: null,
      // Definition object corresponding to the currently selected definition item
      definition: null,
      // Flag indicating whether definition is locally modified
      modified: false,
      // Currently selected definition item
      current: {
        // Definition ID or alias
        reference: null,
        // Definition item
        item: null
      },
      // Pane configuration
      library: {
        visible: false,
      },
      details: {
        visible: Preferences.get("blob:workflow.editorDetailsPane", scope),
        view: null,
        data: null
      },
      // Workflow verification
      checker: {
        visible: false
      },
      // Appearance
      wideContent: Preferences.get("blob:workflow.editorWideContent", scope),
      // Nodes and edges rendered in ReactFlow control
      nodes: [],
      edges: [],
      // ReactFlow control interface
      workflowInterface: {
        setNodes: this.setNodes,
        setEdges: this.setEdges
      },
      // Global shell operations
      shellInterface: {
        canEditRevision: this.canEditRevision,
        toggleWideContent: this.toggleWideContent,
        toggleDetails: this.toggleDetails,
        updateDetails: this.updateDetails,
        markModified: this.markModified,
        saveWorkflow: this.saveWorkflow,
        revertWorkflow: this.revertWorkflow,
        checkWorkflow: this.checkWorkflow,
        switchDefinition: this.switchDefinition,
        reloadDefinitions: this.reloadDefinitions,
        updateProgress: this.updateProgress,
        updatePane: this.updatePane
      }
    };
  }

  componentDidMount() {
    this.loadWorkflowManifest();
    this.loadUserCredentials();

    // Switching revision remounts this shell and takes an unsaved definition with it, and there is
    // no beforeunload guard or draft persistence to fall back on. Answering here is what lets the
    // Blob page ask before it does that, instead of confirming every switch blind.
    if ( this.props.unsavedWork ) {
      this.props.unsavedWork.register(UNSAVED_WORK, this.reportUnsavedWork);
    }

    document.addEventListener("keydown", this.handleKeyDown);
  }

  // The definition may resolve while this tab is hidden (the shell is mounted on every workflow blob),
  // and syncDefinitionPath declines to touch the URL from there. Run the deferred sync the moment the
  // editor becomes the visible tab, so the address bar names the definition on screen and a reload
  // lands back on it.
  componentDidUpdate(prevProps) {
    if ( !prevProps.shown && this.props.shown ) {
      this.syncDefinitionPath(this.state.current && this.state.current.item);
    }
  }

  componentWillUnmount() {
    if ( this.props.unsavedWork ) {
      this.props.unsavedWork.unregister(UNSAVED_WORK, this.reportUnsavedWork);
    }

    document.removeEventListener("keydown", this.handleKeyDown);
  }

  // Read when the page needs the answer, never cached: `modified` moves under four different
  // handlers here, and a pushed copy would go stale at whichever one was forgotten.
  reportUnsavedWork = () => {
    return this.state.modified;
  }

  handleKeyDown = (event) => {
    // Shortcuts only fire while this tab is visible. The Editor/Debugger/
    // Playground shells are all mounted at once (WorkflowsShell toggles
    // visibility via CSS hidden) so the document-level listener stays
    // attached even on other tabs — gate on `shown` so the shortcut is
    // a no-op there and the browser default kicks in.
    if ( !this.props.shown ) return;

    // Command+S / Ctrl+S — save when modified and editable
    if ((event.metaKey || event.ctrlKey) && event.key === 's') {
      event.preventDefault();
      if (this.state.modified && this.canEditRevision()) {
        this.saveWorkflow();
      }
    }

    // Command+R / Ctrl+R — reload the workflow definitions list.
    // Cmd+Shift+R passes through to the browser for a force reload.
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === 'r') {
      if (!this.state.modified) {
        event.preventDefault();
        this.loadWorkflowDefinitions();
      }
    }

    // Command+P / Ctrl+P — show/hide the properties panel.
    // preventDefault overrides the browser's print dialog.
    if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
      event.preventDefault();
      this.toggleDetails();
    }
  }

  /**
   * Data loading
   */

  loadUserCredentials() {
    // Short circuit for read-only access
    if ( !this.props.app.api.haveBlobWriteAccess(this.props.blob) ) {
      return;
    }

    // Query the list of user's credentials
    this.props.app.api.credentialsTargetTargetIdGet("user", "me")
      .then(response => {
        // Update application state
        const credentials = response.data.credentials;
        this.setState({ credentials: credentials });
      }, error => {
        this.setState({
          fatal: true,
          fatalMessage: "Failed to load user credentials: " + error.message
        });
      });
  }

  loadWorkflowManifest() {
    // Indicate progress
    this.updateProgress(true, "Loading workflow manifest...", false, null);

    // Initiate manifest loading
    this.props.app.api.workflowManifestGet(this.props.revision.id, "workflow")
      .then( response => {
        // Extract manifest from the response body
        let manifest = response.data.manifest;

        // Post process manifest data structure
        let converter = new Converter(manifest);
        converter.parseManifest();

        // See if specific definition is desired
        let reference = null;
        const section = this.props.match.params.section;
        const sectionId = this.props.match.params.sectionId;
        if ( "workflow" == section && sectionId ) {
          reference = sectionId;
        }

        // Update component state
        this.setState({
          // Notify the view that the manifest has been fetched
          manifest: manifest,
          // Also use this opportunity to request specific definition to be highlighted
          current: {
            reference: reference,
            item: null
          }
        });

        // Proceed to loading workflow definitions
        this.loadWorkflowDefinitions();
      }, error => {
        this.setState({
          fatal: true,
          fatalMessage: "Failed to load workflow manifest: " + error.message
        });
      });
  }

  loadWorkflowDefinitions() {
    // Indicate progress
    this.updateProgress(true, "Loading workflow definitions...", false, null);

    const body = {
      command: "list_definitions",
      category: "workflow"
    };
    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        const definitions = response.data.definitions;

        // Apply current selection
        let definition = null;
        if ( null != this.state.current.reference ) {
          // Find definition that is referenced
          for ( const item of definitions ) {
            if ( item["alias"] == this.state.current.reference || item["id"] == this.state.current.reference ) {
              definition = item;
              break;
            }
          }

          // Navigate to the root page if reference leads nowhere. The query comes along: it carries
          // `?revision=`, and a path without it sends the reader back to their remembered revision.
          if ( null == definition ) {
            const orgId = this.props.match.params.orgId;
            const blobId = this.props.match.params.blobId;
            const search = this.props.match.search || "";
            Navigation.push(this.props.history, Navigation.workflowPath(orgId, blobId, "workflow") + search);
          }
        }

        // Default to the first definition
        if ( null == definition ) {
          if ( 0 != definitions.length ) {
            definition = definitions[0];
          } else {
            this.createDefaultWorkflow();
            return;
          }
        }

        // Remove progress
        this.updateProgress(false, null, false, null);

        // Notify the view that definitions have been fetched
        const current
          = ( null == definition ) ? {
            reference: null,
            item: null
          } : {
            reference: definition["id"],
            item: definition
          };
        this.setState({
          definitions: definitions,
          current: current
        }, () => {
          // Point the URL at whatever we actually settled on (create / rename / delete all land here).
          this.syncDefinitionPath(definition);

          // Proceed to loading workflow definition
          this.loadWorkflowDefinition();
        });
      }, error => {
        // Render error state
        this.updateProgress(
          false, null, true, "Failed to load definitions: " + error.message);
      });
  }

  createDefaultWorkflow() {
    // Indicate progress
    this.updateProgress(true, "Creating default workflow definition...", false, null);

    // Create "default" definition
    const alias = "default";
    const body = {
      command: "create_definition",
      category: "workflow",
      alias: alias
    };
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        // Retrieve definition
        const definition = response.data["definition"];
        const definitionId = definition["id"];
        const definitions = [ definition ];

        // Notify the view
        this.setState({
          definitions: definitions,
          current: {
            reference: definitionId,
            item: definition
          }
        }, () => {
          this.syncDefinitionPath(definition);
          this.loadWorkflowDefinition();
        });
      }, error => {
        // Indicate fatal failure
        this.updateProgress(
          false, null, true, "Failed to create definition: " + error.message);
      });
  }

  loadWorkflowDefinition() {
    if ( null == this.state.manifest || null == this.state.current.item ) {
      return;
    }

    // Indicate progress
    this.updateProgress(true, `Loading workflow definition "${this.state.current.item.alias}"...`, false, null);

    const body = {
      command: "download_definition",
      definition_id: this.state.current.item["id"]
    };
    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        const definition = response.data.definition_object;

        const converter = new Converter(this.state.manifest);
        const [initialNodes, initialEdges] = converter.blobhubDefinitionToReactFlow(
          definition
        );

        this.setState({
          definition: definition,
          nodes: initialNodes,
          edges: initialEdges,
          modified: false,
          details: {
            visible: this.state.details.visible,
            view: null,
            data: null
          },
          checker: {
            visible: false
          }
        });

        // Remove progress
        this.updateProgress(false, null, false, null);
      }, error => {
        // Render error state
        this.updateProgress(
          false, null, true, "Failed to download definition: " + error.message);
      });
  }

  /**
   * Workflow interface
   */

  setNodes = (nodes, isModified) => {
    this.setState({
      nodes: nodes
    });

    if ( isModified ) {
      this.markModified();
    }
  }

  setEdges = (edges, isModified) => {
    this.setState({
      edges: edges
    });

    if ( isModified ) {
      this.markModified();
    }
  }

  addCount = 0;
  surfaceRef = React.createRef();

  handleReactFlowInit = (instance) => {
    this.rfInstance = instance;
  };

  handleAddComponent = (processorDefinition) => {
    if (!this.rfInstance) return;
    if (!this.canEditRevision()) return;

    // Place at the center of the visible canvas (not the window), so the new
    // node lands centered even with the library/details panes taking width.
    const rect = this.surfaceRef.current?.getBoundingClientRect();
    const screenPoint = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const base = this.rfInstance.screenToFlowPosition(screenPoint);
    const { dx, dy } = cascadeOffset(this.addCount++);
    const position = { x: base.x + dx, y: base.y + dy };

    const converter = new Converter(this.state.manifest);
    const node = converter.createNewNode(processorDefinition, position, this.state.nodes);
    this.setNodes(this.state.nodes.concat(node), true);
  };

  /**
   * Shell interface
   */

  canEditRevision = () => {
    return this.props.app.api.canUpdateRevision(this.props.blob, this.props.revision);
  }

  toggleWideContent = () => {
    const next = !this.state.wideContent;
    const scope = { orgId: this.props.org.id, blobId: this.props.blob.id };
    Preferences.set("blob:workflow.editorWideContent", next, scope);
    this.setState({ wideContent: next });
  }

  toggleDetails = () => {
    let details = this.state.details;
    details.visible = !details.visible;
    this.setState({
      details: details
    });
  }

  updateDetails = (view, data) => {
    let details = this.state.details;
    if ( details.view == view && details.data == data ) {
      return;
    }

    details.view = view;
    details.data = data;
    this.setState({
      details: details
    });
  }

  markModified = () => {
    this.setState({
      modified: true
    });
  }

  saveWorkflow = () => {
    // Display progress
    this.updateProgress(true, "Saving workflow definition...", false, null);

    // Convert ReactFlow representation into BlobHub workflow definition object
    const converter = new Converter(this.state.manifest);
    const definition = converter.reactFlowToBlobhubDefinition(this.state.nodes, this.state.edges);

    // Upload new definition
    const body = {
      command: "upload_definition",
      definition_id: this.state.current.item.id,
      definition: definition
    };
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        // Remove progress
        this.updateProgress(false, null, false, null);

        // Update control state
        this.setState({
          definition: definition,
          modified: false,
          checker: {
            visible: false
          }
        });
      }, error => {
        // Render error state
        this.updateProgress(
          false, null, true, "Failed to save workflow definition: " + error.message);
      });
  }

  checkWorkflow = () => {
    // Display progress
    this.updateProgress(true, "Checking workflow definition for correctness...", false, null);

    // Check definition
    const body = {
      command: "check_definition",
      definition_id: this.state.current.item.id
    };
    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        // Remove progress
        this.updateProgress(false, null, false, null);

        // Update control state
        this.setState({
          checker: {
            visible: true,
            status: response.data.status,
            events: response.data.events
          }
        });
      }, error => {
        // Render error state
        this.updateProgress(
          false, null, true, "Failed to check workflow definition: " + error.message);
      });
  }

  revertWorkflow = () => {
    const converter = new Converter(this.state.manifest);
    const [initialNodes, initialEdges] = converter.blobhubDefinitionToReactFlow(
      this.state.definition
    );

    this.setState({
      nodes: initialNodes,
      edges: initialEdges,
      modified: false
    });
  }

  // Keep the URL pointing at whichever definition is actually selected.
  //
  // switchDefinition() pushes the path when the USER picks a definition, but every other way the
  // selection moves went through loadWorkflowDefinitions(), which resolved a definition into state and
  // left the URL alone. So creating a workflow, renaming one, or deleting one all left the address bar
  // naming the old (or deleted) alias, and a reload would land somewhere else than the screen showed.
  //
  // Replaces rather than pushes: these are corrections to the current location, not navigations the
  // user should have to walk back through. No-ops when the path already names this definition, which
  // is the common case on first load.
  //
  // Only ever a correction while the editor is the tab on screen. All three workflow tabs stay mounted
  // at once and merely hide with CSS, so this shell also resolves a definition when the user opened
  // Playground, Revisions, or Settings directly. The path carries the URL section the Blob page
  // derives the active tab from, so rewriting it from a hidden tab does not correct an alias, it
  // throws the user onto the Workflow tab. componentDidUpdate re-runs the sync when the tab does
  // become visible, so nothing is lost by deferring.
  syncDefinitionPath(definition) {
    if ( !this.props.shown ) {
      return;
    }
    if ( !definition || !definition["alias"] ) {
      return;
    }
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    if ( this.props.match.params.sectionId === definition["alias"] ) {
      return;
    }
    this.props.history.replace(Navigation.workflowPath(orgId, blobId, "workflow", definition["alias"]));
  }

  switchDefinition = (definition) => {
    // Navigate to definition path
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    const sectionId = definition["alias"];
    Navigation.push(this.props.history, Navigation.workflowPath(orgId, blobId, "workflow", sectionId));

    // Notify the view that new definition is selected and is about to be loaded
    this.setState({
      current: {
        reference: definition["id"],
        item: definition
      },
      definition: null
    }, () => {
      // Load definition itself
      this.loadWorkflowDefinition();
    });
  }

  reloadDefinitions = (reference) => {
    // Clear current definition data keeping selected definition applied
    this.setState({
      current: {
        reference: reference,
        item: null
      },
      definition: null,
      definitions: null
    });

    // Reload the list
    this.loadWorkflowDefinitions();
  }

  updateProgress = (progress, progressMessage, error, errorMessage) => {
    this.setState({
      progress: progress,
      progressMessage: progressMessage,
      error: error,
      errorMessage: errorMessage
    });
  }

  updatePane = (pane, visible) => {
    let paneObject = this.state[pane];
    paneObject.visible = visible;
    this.setState({pane: paneObject});
  }

  /**
   * UX
   */

  render() {
    if ( this.state.fatal ) {
      return (
        <>
          <Container className="blob-header-row content-row">
            <Alert variant="warning">
              {this.state.fatalMessage}
            </Alert>
          </Container>
        </>
      );
    }

    const containerContentClass
      = this.state.wideContent || this.state.library.visible || this.state.details.visible
      ? "content-row-wide"
      : "content-row";

    // In collapsed mode the selector row is hidden and the editor surface fills
    // the pane, so progress/error alerts FLOAT over the top of the surface
    // instead of pushing it down (which grew the page and jumped the screen).
    // See `.blob-content-pane-floating` in Blob/index.css.
    const paneClass = "blob-content-pane" + (this.props.headerCollapsed ? " blob-content-pane-floating" : "");

    return (
      <>
        {(!this.state.progress && null != this.state.definitions && !this.props.headerCollapsed ) &&
          <Selector
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            shell={this.state}
            history={this.props.history}
            match={this.props.match}
          />
        }

        <div className={paneClass}>
          {this.state.progress &&
            <Container className="blob-header-row content-row blob-pane-alert">
              <Alert variant="primary">
                {this.state.progressMessage}
              </Alert>
            </Container>
          }

          {this.state.error &&
            <Container className="blob-header-row content-row blob-pane-alert">
              <Alert variant="warning">
                {this.state.errorMessage}
                <span className="float-right">
                  <ButtonGroup size="sm" className="header-options">
                    <Button variant="light" className="btn-warning-close" onClick={(event) => {
                      this.updateProgress(false, null, false, null);
                    }}><GoX/></Button>
                  </ButtonGroup>
                </span>
              </Alert>
            </Container>
          }

          {null != this.state.definition &&
            <Container className={"blob-header-row " + containerContentClass}>
              <Row className={this.state.progress ? "make-readonly" : ""}>
                <Col sm={3} className={`detail-panel-col ${this.state.library.visible ? "" : "hidden"}`}>
                  <LibraryPane shell={this.state} onAddComponent={this.handleAddComponent} />
                </Col>
                <Col>
                  <EditorPane
                    app={this.props.app}
                    blob={this.props.blob}
                    revision={this.props.revision}
                    shell={this.state}
                    shown={this.props.shown}
                    onReactFlowInit={this.handleReactFlowInit}
                    surfaceRef={this.surfaceRef}
                  />
                </Col>
                <Col sm={3} className={`detail-panel-col ${this.state.details.visible ? "" : "hidden"}`}>
                  <DetailsPane
                    app={this.state}
                    blob={this.props.blob}
                    revision={this.props.revision}
                    shell={this.state}
                  />
                </Col>
              </Row>
            </Container>
          }
        </div>

        {this.state.checker.visible &&
          <CheckerPane
            app={this.state}
            blob={this.props.blob}
            revision={this.props.revision}
            shell={this.state}
          />
        }
      </>
    )
  }
}
